import { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { Dropdown, ProductTabs } from 'components';

import Logo from '../Logo';
import {
  routes,
  locales,
  PRODUCTS,
  resolveActiveProduct,
} from 'constants/index';
import { useUserQuery } from 'modules/App/queries';
import { setUYTag, getUYTag } from 'utils';

import './header.scss';

// The server names years "UY-2026-2027"; read as "UY 2026-2027". Display only,
// the value stays as sent.
function formatYear(label?: string): string {
  return (label ?? '').replace(/^UY-/, 'UY ');
}

function initialsOf(firstname = '', lastname = ''): string {
  return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase();
}

interface HeaderProps {
  isRolePage: boolean;
  // Below 768px the sidebar is off-canvas, so the header carries its trigger.
  onMenuOpen?: () => void;
}

/*
 * Carries one mark only, and no client logo appears anywhere in the frame. On a
 * desktop the mark heads the sidebar, so the header shows it only where there
 * is no sidebar beside it: on the role page, and on a phone.
 *
 * Two levels, two surfaces: the products run across the header, the sections of
 * the active product run down the sidebar. The header also holds account-level
 * things: which underwriting year you are looking at, and who you are. Below
 * 768px the sidebar is off-canvas and the header holds the button that opens
 * it, and the identity folds into an icon so everything fits on two rows.
 */
export default function Header({ isRolePage, onMenuOpen }: HeaderProps) {
  const userQuery = useUserQuery();
  const { pathname } = useLocation();

  //* Local State
  const [isMenuOpened, setMenuVisibility] = useState(false);
  const [selectedUYTag, setSelectedUYTag] = useState(getUYTag());

  const userMenuRef = useRef<HTMLDivElement>(null);
  const userTriggerRef = useRef<HTMLButtonElement>(null);

  const userType = userQuery.data?.data.data.user.userTypes?.find(
    ({ id }) => id === userQuery.data?.data.data.user.userTypeId
  );

  const userName = userQuery.data?.data.data.user.name;

  // Newest year first, so it is also the default pick below. rbac returns
  // the tags in no guaranteed order, and "uy-YYYY-YYYY" keys sort by year.
  const uyTagsOptions = userType?.uytags
    .map((item) => ({
      label: item.value,
      value: item.key,
    }))
    .sort((a, b) => b.value.localeCompare(a.value));

  const storedUYTag = getUYTag() || '';

  if (storedUYTag.length === 0 && uyTagsOptions && uyTagsOptions?.length > 0) {
    const defaultTag = uyTagsOptions?.[0].value || '';

    setUYTag(defaultTag);
    setSelectedUYTag(defaultTag);
  }

  // The user menu closes like every other dropdown here: Escape (returning
  // focus to its trigger), or a press outside.
  useEffect(() => {
    if (!isMenuOpened) return;

    function handleOutsidePress(event: MouseEvent | TouchEvent) {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setMenuVisibility(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenuVisibility(false);
        userTriggerRef.current?.focus();
      }
    }

    document.addEventListener('mousedown', handleOutsidePress);
    document.addEventListener('touchstart', handleOutsidePress);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsidePress);
      document.removeEventListener('touchstart', handleOutsidePress);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpened]);

  //* Handlers
  function onMenuClick() {
    setMenuVisibility(!isMenuOpened);
  }

  function handleYUTagsChange(event: any) {
    setUYTag(event.value);
    setSelectedUYTag(event.value);

    window.location.reload();
  }

  const hasIdentity = !isRolePage && !!userName;
  const fullName = `${userName?.firstname ?? ''} ${userName?.lastname ?? ''}`.trim();
  const identity = userType?.name ? `${fullName}, ${userType.name}` : fullName;

  return (
    <div className={classNames('header', { 'header--bare': isRolePage })}>
      <NavLink
        to={isRolePage ? routes.role : routes.main}
        className="header__logo-link"
      >
        <Logo className="header__logo" />
      </NavLink>

      {!isRolePage && (
        <button
          type="button"
          className="header__menu-button"
          onClick={onMenuOpen}
          aria-label="Open navigation"
        >
          <i className="pi pi-bars" aria-hidden="true" />
        </button>
      )}

      {!isRolePage && (
        <ProductTabs
          products={PRODUCTS}
          active={
            pathname.startsWith(routes.docs.index)
              ? undefined
              : resolveActiveProduct(pathname)
          }
        />
      )}

      <div className="header__spacer" />

      <div className="header__actions-wrapper flex flex-align-items-center">
        {!isRolePage && uyTagsOptions && uyTagsOptions.length > 0 && (
          <div className="header__yu-tags">
            <i className="header__yu-tags-icon pi pi-calendar" aria-hidden="true" />
            <Dropdown
              id="uyTag"
              name="uyTag"
              value={selectedUYTag}
              onChange={handleYUTagsChange}
              options={uyTagsOptions}
              valueTemplate={(option: any) => formatYear(option?.label)}
              itemTemplate={(option: any) => formatYear(option?.label)}
            />
          </div>
        )}

        <div className="header__user" ref={userMenuRef}>
          <button
            ref={userTriggerRef}
            type="button"
            className="header__user-trigger"
            aria-haspopup="true"
            aria-expanded={isMenuOpened}
            aria-label={hasIdentity ? `Account menu for ${identity}` : 'Account menu'}
            title={hasIdentity ? identity : undefined}
            onClick={onMenuClick}
          >
            {/* The initials alone: the menu names who is signed in. */}
            {hasIdentity ? (
              <span className="header__avatar" aria-hidden="true">
                {initialsOf(userName?.firstname, userName?.lastname)}
              </span>
            ) : (
              <i className="header__user-icon pi pi-user" aria-hidden="true" />
            )}
          </button>

          {isMenuOpened && (
            <div
              className="dropdown flex flex-column"
              role="presentation"
              onClick={() => setMenuVisibility(false)}
            >
              {/* The trigger shows only the initials, so the menu opens on who
                  is signed in and the user type they are acting as. */}
              {!!userName && (
                <div className="dropdown__identity">
                  <span className="dropdown__identity-name">{fullName}</span>
                  {!!userType?.name && (
                    <span className="dropdown__identity-role">
                      {userType.name}
                    </span>
                  )}
                </div>
              )}

              {userQuery.data?.data.data.user.userTypes &&
                userQuery.data?.data.data.user.userTypes?.length > 1 && (
                  <Link to={`${routes.role}`} className="dropdown__item">
                    {locales.general.changeUserType}
                  </Link>
                )}

              <Link
                to={`${routes.authentication.index}/${routes.authentication.resetPin}`}
                className="dropdown__item"
              >
                {locales.general.resetPin}
              </Link>

              {!!userType?.buttons.find(
                ({ identifier }) => identifier === 'redirect-to-admin'
              ) && (
                <Link to={routes.admin} className="dropdown__item">
                  {locales.general.admin}
                </Link>
              )}

              <Link
                to={`${routes.docs.index}/${routes.docs.workflow}`}
                className="dropdown__item"
              >
                {locales.general.docs}
              </Link>

              <Link
                to={`${routes.authentication.index}/${routes.authentication.logout}`}
                className="dropdown__item dropdown__item--logout"
              >
                {locales.general.logout}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
